import React, { type FC } from 'react';

import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';

import Placeholder from '@atlassian/jira-placeholder';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { expValEquals } from '@atlassian/jira-feature-experiments';

import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import {
	useIsEligibleForTrialAssistantReminder,
	useJiraEntitlementId,
} from '../../services/eligibility-check/index.tsx';
import { useOpenTrialAssistantAgent } from '../../services/rovo-trigger/index.tsx';
import { ManualTrialCondition } from '../common/ManualTrialCondition.tsx';
import type ReminderNudgeType from './index.tsx';

const AsyncLazyReminderNudge = lazyAfterPaint<typeof ReminderNudgeType>(() =>
	import(/* webpackChunkName: "async-jira-trial-assistant-reminder-nudge" */ './index.tsx').then(
		(module) => module.ReminderNudge,
	),
);

type JiraTrialAssistantReminderNudgeProps = {
	targetId: string;
};

const JiraTrialAssistantReminderAsyncWrapper: FC<JiraTrialAssistantReminderNudgeProps> = ({
	targetId,
}) => {
	const triggerRovo = useOpenTrialAssistantAgent('jira-trial-reminder-spotlight');

	if (!expValEquals('custom_rovo_onboarding_agent', 'cohort', 'variation', 'not-enrolled')) {
		return <></>;
	}

	return (
		<JSErrorBoundary
			id="JiraTrialAssistantReminderNudge"
			packageName={PACKAGE_NAME}
			teamName={TEAM_NAME}
			fallback="unmount"
		>
			<UFOSegment name="jira-trial-assistant-reminder-nudge">
				<Placeholder name="jira-trial-assistant-reminder-nudge" fallback={null}>
					<AsyncLazyReminderNudge targetId={targetId} onPrimaryCta={triggerRovo} />
				</Placeholder>
			</UFOSegment>
		</JSErrorBoundary>
	);
};

export const JiraTrialAssistantReminderNudge: FC<JiraTrialAssistantReminderNudgeProps> = ({
	targetId,
}) => {
	const jiraEntitlementId = useJiraEntitlementId();
	const isEligible = useIsEligibleForTrialAssistantReminder() && !!jiraEntitlementId;

	if (!isEligible) {
		return <></>;
	}

	return (
		<ManualTrialCondition entitlementId={jiraEntitlementId}>
			<JiraTrialAssistantReminderAsyncWrapper targetId={targetId} />
		</ManualTrialCondition>
	);
};
